import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  ArrowLeft, 
  Save, 
  Send, 
  Trash2,
  CheckCircle2,
  MessageSquare,
  User,
  Paperclip,
  FileText,
  X,
  Download,
  ExternalLink,
  Image as ImageIcon,
  MessageCircle,
  ThumbsUp,
  Route,
  ChevronRight,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "react-hot-toast";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";

const getEditorTitle = (isNew, role) => {
  if (isNew) return 'New Journal Entry';
  if (role === 'teacher') return 'Review Journal';
  return 'Edit Journal';
};

const JournalStatusBadge = ({ status }) => {
  if (status === 'reviewed') return <Badge variant="default">Reviewed</Badge>;
  if (status === 'submitted') return <Badge variant="secondary">Pending Review</Badge>;
  return <Badge variant="outline">Draft</Badge>;
};

export default function JournalEditor({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNew = !id || id === 'new';
  const commentsRef = useRef(null);
  const [highlightComments, setHighlightComments] = useState(false);
  
  const [journal, setJournal] = useState(null);
  const [student, setStudent] = useState(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [feedback, setFeedback] = useState('');
  const [growthStagePreview, setGrowthStagePreview] = useState(
    isNew && searchParams.get('growth_stage_id')
      ? { stage_id: searchParams.get('growth_stage_id'), title: searchParams.get('stage_title'), order_index: null, is_completed: false }
      : null
  );
  const growthStageId = isNew ? searchParams.get('growth_stage_id') : null;
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ percent: 0, status: '' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!isNew) {
      fetchJournal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  // Handle highlight and scroll from notification
  useEffect(() => {
    const highlight = searchParams.get('highlight');
    if (highlight && !loading && journal) {
      // Use a longer delay and retry mechanism for DOM elements
      const tryScrollAndHighlight = (attempts = 0) => {
        if (highlight === 'comments') {
          // Check if comments section exists in DOM
          if (commentsRef.current) {
            commentsRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setHighlightComments(true);
            setTimeout(() => setHighlightComments(false), 2500);
          } else if (attempts < 5) {
            // Retry after a short delay if element not ready
            setTimeout(() => tryScrollAndHighlight(attempts + 1), 200);
          }
        } else if (highlight === 'content') {
          // Scroll to top of journal content
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      };
      
      // Start with initial delay to allow DOM to render
      const timer = setTimeout(() => tryScrollAndHighlight(), 300);
      return () => clearTimeout(timer);
    }
  }, [searchParams, loading, journal, comments]);

  const fetchJournal = async () => {
    try {
      const response = await axios.get(`${API}/journals/${id}`);
      setJournal(response.data);
      setTitle(response.data.title);
      setContent(response.data.content);
      setAttachments(response.data.attachments || []);
      setGrowthStagePreview(response.data.growth_stage_preview || null);
      
      // Fetch comments
      try {
        const commentsRes = await axios.get(`${API}/journals/${id}/comments`);
        setComments(commentsRes.data);
      } catch (err) {
        console.log("No comments yet or error fetching comments");
      }
      
      // If teacher, fetch student info
      if (user.role === 'teacher' && response.data.student_id) {
        try {
          const studentRes = await axios.get(`${API}/users/${response.data.student_id}`);
          setStudent(studentRes.data);
        } catch (err) {
          console.error("Error fetching student:", err);
        }
      }
    } catch (error) {
      console.error("Error fetching journal:", error);
      toast.error("Failed to load journal");
      navigate('/journals');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    setUploadProgress({ percent: 0, status: 'Preparing...' });
    
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);

        // Stage 1: Uploading (0-40%)
        setUploadProgress({ percent: 10, status: 'Uploading file...' });
        
        const response = await axios.post(`${API}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 40) / progressEvent.total);
            setUploadProgress({ percent: percentCompleted, status: 'Uploading file...' });
          }
        });

        setUploadProgress({ percent: 40, status: 'File uploaded' });

        const isImage = file.type.startsWith('image/');
        const isPdf = file.name.toLowerCase().endsWith('.pdf');
        const isDocx = file.name.toLowerCase().endsWith('.docx');
        const fileId = response.data.file_id;
        
        setAttachments(prev => [...prev, {
          file_id: fileId,
          filename: file.name,
          type: isImage ? 'image' : 'document',
          url: `/api/files/${fileId}`
        }]);

        // Only for students: Extract text from PDF, JPEG, PNG, DOCX
        if (user.role === 'student' && (isImage || isPdf || isDocx)) {
          const ext = file.name.split('.').pop().toLowerCase();
          if (['pdf', 'jpg', 'jpeg', 'png', 'docx'].includes(ext)) {
            setExtracting(true);
            
            // Stage 2: Reading document (40-90%)
            setUploadProgress({ percent: 50, status: 'Reading document...' });
            
            // Simulate progress during OCR
            const progressInterval = setInterval(() => {
              setUploadProgress(prev => ({
                percent: Math.min(prev.percent + 5, 85),
                status: 'Reading document...'
              }));
            }, 500);
            
            try {
              const extractResponse = await axios.post(`${API}/extract-text/${fileId}`);
              
              clearInterval(progressInterval);
              setUploadProgress({ percent: 90, status: 'Processing text...' });
              
              if (extractResponse.data.success && extractResponse.data.text) {
                const extractedText = extractResponse.data.text;
                const sourceLabels = { pdf: 'PDF', docx: 'Word Document' };
                const sourceType = sourceLabels[extractResponse.data.source_type] || 'image';
                
                // Append extracted text to content with a note
                const separator = content.trim() ? '\n\n' : '';
                const noteText = `${separator}--- Text extracted from ${file.name} (${sourceType}) ---\n${extractedText}`;
                
                setContent(prev => prev + noteText);
                setUploadProgress({ percent: 100, status: 'Complete!' });
                toast.success(`Text extracted from ${file.name}`);
              } else {
                setUploadProgress({ percent: 100, status: 'Complete!' });
              }
            } catch (extractError) {
              clearInterval(progressInterval);
              console.error("Text extraction error:", extractError);
              setUploadProgress({ percent: 100, status: 'Could not read document' });
              toast.error(`Could not extract text from ${file.name}`);
            } finally {
              setExtracting(false);
            }
          } else {
            setUploadProgress({ percent: 100, status: 'Complete!' });
          }
        } else {
          setUploadProgress({ percent: 100, status: 'Complete!' });
        }
      }
      
      // Reset progress after a short delay
      setTimeout(() => {
        setUploadProgress({ percent: 0, status: '' });
      }, 1500);
      
    } catch (error) {
      console.error("Upload error:", error);
      setUploadProgress({ percent: 0, status: 'Upload failed' });
      toast.error("Failed to upload file");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const saveJournal = async (status = 'draft') => {
    if (!title.trim()) {
      toast.error("Please enter a title");
      return;
    }
    if (!content.trim()) {
      toast.error("Please write something in your journal");
      return;
    }

    setSaving(true);
    try {
      const journalData = {
        title,
        content,
        status,
        attachments: attachments.map(a => ({
          file_id: a.file_id,
          filename: a.filename,
          type: a.type,
          url: a.url
        })),
        ...(isNew && growthStageId ? { growth_stage_id: growthStageId } : {})
      };

      if (isNew) {
        const response = await axios.post(`${API}/journals`, journalData);
        toast.success(status === 'submitted' ? "Journal submitted!" : "Journal saved as draft");
        navigate(`/journals/${response.data.journal_id}`);
      } else {
        await axios.put(`${API}/journals/${id}`, journalData);
        toast.success(status === 'submitted' ? "Journal submitted!" : "Journal updated");
        fetchJournal();
      }
    } catch (error) {
      console.error("Error saving journal:", error);
      toast.error("Failed to save journal");
    } finally {
      setSaving(false);
    }
  };

  const deleteJournal = async () => {
    try {
      await axios.delete(`${API}/journals/${id}`);
      toast.success("Journal deleted");
      navigate('/journals');
    } catch (error) {
      console.error("Error deleting journal:", error);
      toast.error("Failed to delete journal");
    }
  };

  const submitComment = async () => {
    if (!newComment.trim()) {
      toast.error("Please enter a comment");
      return;
    }

    setSubmittingComment(true);
    try {
      await axios.post(`${API}/journals/${id}/comments`, { content: newComment });
      toast.success("Comment added");
      setNewComment('');
      // Refresh comments
      const commentsRes = await axios.get(`${API}/journals/${id}/comments`);
      setComments(commentsRes.data);
    } catch (error) {
      console.error("Error submitting comment:", error);
      toast.error("Failed to add comment");
    } finally {
      setSubmittingComment(false);
    }
  };

  const deleteComment = async (commentId) => {
    try {
      await axios.delete(`${API}/journals/comments/${commentId}`);
      toast.success("Comment deleted");
      setComments(prev => prev.filter(c => c.comment_id !== commentId));
    } catch (error) {
      console.error("Error deleting comment:", error);
      toast.error("Failed to delete comment");
    }
  };

  const submitFeedback = async () => {
    if (!feedback.trim()) {
      toast.error("Please enter feedback");
      return;
    }

    try {
      await axios.post(`${API}/journals/${id}/feedback`, { feedback });
      toast.success("Feedback submitted!");
      setFeedback('');
      fetchJournal();
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast.error("Failed to submit feedback");
    }
  };

  const acknowledgeJournal = async () => {
    setAcknowledging(true);
    try {
      await axios.post(`${API}/journals/${id}/acknowledge`);
      toast.success("Journal acknowledged!");
      fetchJournal();
    } catch (error) {
      console.error("Error acknowledging journal:", error);
      toast.error("Failed to acknowledge journal");
    } finally {
      setAcknowledging(false);
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-slate-300 border-t-amber-500 rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  const isEditable = user.role === 'student' && (!journal || journal.status === 'draft');
  // Student can only submit if journal is a draft (not already submitted or reviewed)
  const canSubmit = user.role === 'student' && (!journal || journal.status === 'draft');

  return (
    <Layout user={user}>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/journals')}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-serif text-2xl font-bold text-slate-900">
              {getEditorTitle(isNew, user.role)}
            </h1>
          </div>
          {journal && <JournalStatusBadge status={journal.status} />}
        </div>

        {growthStagePreview && (
          <button
            type="button"
            onClick={() => {
              const sid = journal?.student_id || user.user_id;
              const path = user.role === 'teacher'
                ? `/growth-plan/${sid}?stage_id=${growthStagePreview.stage_id}`
                : `/growth-plan?stage_id=${growthStagePreview.stage_id}`;
              navigate(path);
            }}
            data-testid="journal-growth-stage-preview-card"
            className="flex items-center gap-3 w-fit max-w-full p-3 rounded-lg border border-teal-200 bg-teal-50 hover:bg-teal-100 transition-colors text-left"
          >
            <div className="w-9 h-9 rounded-full bg-teal-100 flex items-center justify-center shrink-0">
              <Route className="w-4 h-4 text-teal-600" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-teal-900">Growth Plan</p>
              <p className="text-xs text-teal-600 truncate">
                {growthStagePreview.order_index != null ? `Stage ${growthStagePreview.order_index + 1}: ` : ''}{growthStagePreview.title}
                {growthStagePreview.is_completed && ' · Completed'}
              </p>
            </div>
            <ChevronRight className="w-4 h-4 text-teal-400 shrink-0" />
          </button>
        )}

        {/* Student Info for Teachers */}
        {user.role === 'teacher' && student && (
          <Card className="border-slate-100 bg-slate-50/50">
            <CardContent className="py-4">
              <div className="flex items-center gap-4">
                <Avatar className="w-12 h-12">
                  <AvatarImage src={student.picture} />
                  <AvatarFallback className="bg-amber-100 text-amber-700">
                    {getInitials(student.name)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium text-slate-900">{student.name}</p>
                  <p className="text-sm text-slate-500">{student.email}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="ml-auto rounded-full"
                  onClick={() => navigate(`/students/${student.user_id}`)}
                >
                  <User className="w-4 h-4 mr-1" />
                  View Profile
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Editor */}
        <Card className="border-slate-100">
          <CardContent className="p-6 space-y-6">
            {/* Title */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Title</label>
              {user.role === 'teacher' && !isNew ? (
                <h2 className="font-serif text-xl text-slate-900">{title || 'Untitled'}</h2>
              ) : (
                <Input
                  data-testid="journal-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Give your journal entry a title..."
                  className="font-serif text-lg"
                  disabled={!isEditable && user.role === 'student'}
                />
              )}
            </div>

            {/* Content */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">
                {user.role === 'teacher' ? 'Journal Entry' : 'Your Thoughts'}
              </label>
              {user.role === 'teacher' && !isNew ? (
                <div className="prose prose-slate max-w-none">
                  <div className="whitespace-pre-wrap text-slate-700 leading-relaxed bg-slate-50 rounded-lg p-4 min-h-[200px]">
                    {content || 'No content'}
                  </div>
                </div>
              ) : (
                <Textarea
                  data-testid="journal-content"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Write your thoughts, prayers, reflections..."
                  className="min-h-[300px] journal-content resize-none"
                  disabled={!isEditable && user.role === 'student'}
                />
              )}
            </div>

            {/* Attachments Section */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block flex items-center gap-2">
                <Paperclip className="w-4 h-4" />
                Attachments
              </label>
              
              {/* Existing Attachments */}
              {attachments.length > 0 && (
                <div className="space-y-2 mb-4">
                  {attachments.map((att, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200"
                    >
                      {att.type === 'image' ? (
                        <ImageIcon className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      ) : (
                        <FileText className="w-5 h-5 text-amber-600 flex-shrink-0" />
                      )}
                      <span className="flex-1 text-sm text-slate-700 truncate">
                        {att.filename}
                      </span>
                      <div className="flex items-center gap-2">
                        {/* View in Browser */}
                        <a
                          href={att.file_id ? `${API}/files/${att.file_id}?disposition=inline` : att.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 hover:bg-slate-200 rounded-lg transition-colors text-slate-600 hover:text-slate-900"
                          title="View in browser"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                        {/* Download */}
                        <a
                          href={att.file_id ? `${API}/files/${att.file_id}` : att.url}
                          download={att.filename}
                          className="p-2 hover:bg-slate-200 rounded-lg transition-colors text-slate-600 hover:text-slate-900"
                          title="Download"
                        >
                          <Download className="w-4 h-4" />
                        </a>
                        {/* Remove (only for editable) */}
                        {isEditable && (
                          <button
                            onClick={() => removeAttachment(idx)}
                            className="p-2 hover:bg-red-100 rounded-lg transition-colors text-slate-400 hover:text-red-600"
                            title="Remove"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload Button (only for editable) */}
              {isEditable && (
                <div className="space-y-3">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    multiple
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp,.txt"
                    className="hidden"
                  />
                  
                  {/* Progress Bar - Show when uploading or extracting */}
                  {(uploading || extracting) && uploadProgress.status && (
                    <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-slate-700">
                          {uploadProgress.status}
                        </span>
                        <span className="text-sm text-slate-500">
                          {uploadProgress.percent}%
                        </span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-2.5 overflow-hidden">
                        <div 
                          className="h-2.5 rounded-full transition-all duration-300 ease-out"
                          style={{ 
                            width: `${uploadProgress.percent}%`,
                            backgroundColor: uploadProgress.percent === 100 ? '#16a34a' : '#d97706'
                          }}
                        ></div>
                      </div>
                      {extracting && (
                        <p className="text-xs text-slate-500 mt-2 flex items-center">
                          <div className="w-3 h-3 border-2 border-amber-300 border-t-amber-600 rounded-full animate-spin mr-2"></div>
                          AI is reading your document...
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* Upload Button */}
                  {!uploading && !extracting && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      className="rounded-full"
                    >
                      <Paperclip className="w-4 h-4 mr-2" />
                      Add Attachment
                    </Button>
                  )}
                  
                  <p className="text-xs text-slate-500">
                    Supported: PDF, Word, Images (max 10MB)
                  </p>
                  <p className="text-xs text-amber-600">
                    PDF, Word (.docx), JPEG, and PNG files will have text automatically extracted
                  </p>
                </div>
              )}

              {/* Attachments for Teacher View (read-only) */}
              {user.role === 'teacher' && attachments.length === 0 && (
                <p className="text-sm text-slate-500">No attachments</p>
              )}
            </div>

            {/* Action Buttons for Students */}
            {user.role === 'student' && (
              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-slate-100">
                {isEditable && (
                  <Button
                    data-testid="save-draft-btn"
                    variant="outline"
                    onClick={() => saveJournal('draft')}
                    disabled={saving}
                    className="rounded-full"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save as Draft
                  </Button>
                )}
                {canSubmit && (
                  <Button
                    data-testid="submit-journal-btn"
                    onClick={() => saveJournal('submitted')}
                    disabled={saving}
                    className="bg-amber-600 hover:bg-amber-700 rounded-full"
                  >
                    {saving ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit for Review
                      </>
                    )}
                  </Button>
                )}
                {!isNew && user.role === 'student' && (
                  <Button
                    variant="ghost"
                    onClick={() => setDeleteDialogOpen(true)}
                    data-testid="delete-journal-btn"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-full ml-auto"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Feedback Section */}
        {journal?.feedback && (
          <Card className="border-green-200 bg-green-50/50">
            <CardHeader>
              <CardTitle className="text-lg flex items-center text-green-800">
                <CheckCircle2 className="w-5 h-5 mr-2" />
                Teacher Feedback
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-green-800 whitespace-pre-wrap">{journal.feedback}</p>
            </CardContent>
          </Card>
        )}

        {/* Teacher Feedback Form */}
        {user.role === 'teacher' && journal && journal.status === 'submitted' && (
          <Card className="border-slate-100">
            <CardHeader>
              <CardTitle className="text-lg flex items-center">
                <MessageSquare className="w-5 h-5 mr-2 text-slate-600" />
                Review Journal
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                data-testid="teacher-feedback-input"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Write your feedback and encouragement... (optional)"
                className="min-h-[120px]"
              />
              <div className="flex flex-wrap gap-3">
                <Button
                  data-testid="submit-feedback-btn"
                  onClick={submitFeedback}
                  className="bg-green-600 hover:bg-green-700 rounded-full"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Submit Feedback
                </Button>
                <Button
                  data-testid="acknowledge-journal-btn"
                  variant="outline"
                  onClick={acknowledgeJournal}
                  disabled={acknowledging}
                  className="rounded-full border-slate-300 text-slate-700 hover:bg-slate-50"
                >
                  {acknowledging ? (
                    <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin mr-2" />
                  ) : (
                    <ThumbsUp className="w-4 h-4 mr-2" />
                  )}
                  Acknowledge
                </Button>
              </div>
              <p className="text-xs text-slate-400">
                Use <strong>Submit Feedback</strong> to leave a comment, or <strong>Acknowledge</strong> to simply mark as reviewed without a message.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Comments/Discussion Section - Only show after feedback has been submitted */}
        {!isNew && journal && journal.status === 'reviewed' && (
          <Card 
            ref={commentsRef}
            className={`border-slate-100 transition-all duration-300 ${
              highlightComments ? 'notification-highlight ring-2 ring-amber-400 ring-offset-2' : ''
            }`}
          >
            <CardHeader>
              <CardTitle className="text-lg flex items-center">
                <MessageCircle className="w-5 h-5 mr-2 text-blue-600" />
                Discussion ({comments.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Existing Comments */}
              {comments.length > 0 && (
                <div className="space-y-3">
                  {comments.map((comment) => (
                    <div 
                      key={comment.comment_id}
                      className="p-3 bg-slate-50 rounded-lg border border-slate-100"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2 mb-2">
                          <Avatar className="w-6 h-6">
                            <AvatarImage src={comment.user_picture} />
                            <AvatarFallback className="text-xs bg-blue-100 text-blue-700">
                              {(comment.user_name || comment.author_name)?.charAt(0) || '?'}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-sm font-medium text-slate-900">
                            {comment.user_name || comment.author_name || 'Unknown'}
                          </span>
                          <span className="text-xs text-slate-500">
                            {format(new Date(comment.created_at), 'MMM d, h:mm a')}
                          </span>
                        </div>
                        {(comment.user_id === user.user_id || comment.author_id === user.user_id) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteComment(comment.comment_id)}
                            data-testid={`delete-comment-btn-${comment.comment_id}`}
                            className="h-6 w-6 p-0 text-slate-400 hover:text-red-600"
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                      <p className="text-sm text-slate-700">{comment.content}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Add New Comment */}
              <div className="flex gap-2">
                <Textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  data-testid="new-comment-input"
                  className="min-h-[80px] flex-1"
                />
              </div>
              <Button
                onClick={submitComment}
                disabled={submittingComment || !newComment.trim()}
                data-testid="post-comment-btn"
                className="rounded-full"
              >
                {submittingComment ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                    Posting...
                  </>
                ) : (
                  <>
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Post Comment
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onConfirm={deleteJournal}
          title="Delete Journal?"
          description="This will permanently delete this journal entry. This action cannot be undone."
          confirmText="Delete Journal"
        />
      </div>
    </Layout>
  );
}
